import type { ThemeTokens } from './tokens';
import { getShapeRadii } from './tokens';

const clamp = (value: number, min = 0, max = 255) =>
    Math.min(max, Math.max(min, value));

const normalizeHex = (hex: string): string => {
    const cleaned = hex.trim().replace('#', '');
    if (cleaned.length === 3) {
        return cleaned
            .split('')
            .map((char) => `${char}${char}`)
            .join('');
    }
    return cleaned.slice(0, 6);
};

export const hexToRgb = (hex: string) => {
    const normalized = normalizeHex(hex);
    const int = Number.parseInt(normalized, 16);
    return {
        r: clamp((int >> 16) & 255),
        g: clamp((int >> 8) & 255),
        b: clamp(int & 255),
    };
};

export const rgbToHex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map((channel) => clamp(channel).toString(16).padStart(2, '0')).join('')}`;

export const mixColors = (colorA: string, colorB: string, weight = 0.5) => {
    const a = hexToRgb(colorA);
    const b = hexToRgb(colorB);
    const ratio = clamp(weight, 0, 1);

    return rgbToHex(
        Math.round(a.r * (1 - ratio) + b.r * ratio),
        Math.round(a.g * (1 - ratio) + b.g * ratio),
        Math.round(a.b * (1 - ratio) + b.b * ratio),
    );
};

export const withAlpha = (hex: string, alpha: number) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
};

export const buildDerivedTokens = (tokens: ThemeTokens) => {
    const radii = getShapeRadii(tokens.shape);

    return {
        primaryLight: mixColors(tokens.primary, '#ffffff', 0.88),
        primaryLight25: mixColors(tokens.primary, '#ffffff', 0.85),
        primaryLightDark: mixColors(tokens.primary, '#ffffff', 0.68),
        secondaryLight: mixColors(tokens.secondary, '#ffffff', 0.72),
        bgLight: '#ffffff',
        bgLight25: mixColors(tokens.primary, '#ffffff', 0.84),
        surface: '#ffffff',
        radii,
    };
};

export const isValidHexColor = (value: string) =>
    /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
